/**
 * Location-based Product Filter for Shopify Collections
 * Uses Storefront API to fetch products with location metafields
 * Filters products by distance from user's location
 */

class LocationFilter {
  constructor() {
    this.userLocation = null;

    // Check for saved distance in sessionStorage, otherwise use default
    const savedDistance = sessionStorage.getItem('selectedDistance');
    this.maxDistance = savedDistance ? parseInt(savedDistance) : 5;
    console.log('🎯 LocationFilter initialized with distance:', this.maxDistance, 'miles');

    this.allProducts = [];
    this.filteredProducts = [];
    this.displayedProductIds = new Set(); // Track displayed products for infinite scroll
    this.currentCursor = null;
    this.hasNextPage = false;
    this.isLoading = false;
    this.collectionHandle = this.getCollectionHandle();
    this.pageSize = 50; // Fetch more products at once for client-side filtering

    this.init();
  }

  init() {
    // Check if we're on a collection page
    if (!this.collectionHandle) return;

    // Inject wishlist styles if not already present
    this.injectWishlistStyles();

    // Hide original product grid and pagination
    this.hideOriginalProductGrid();

    // Request location on page load
    this.requestLocation();

    // Set up event listeners
    this.setupEventListeners();
  }

  hideOriginalProductGrid() {
    // Hide the original server-rendered product grid
    const originalGrid = document.querySelector('.product-grid[data-testid="product-grid"]');
    if (originalGrid) {
      // Hide all original product items
      const productItems = originalGrid.querySelectorAll('.product-grid__item');
      productItems.forEach(item => {
        item.style.display = 'none';
      });
      console.log(`Hidden ${productItems.length} original products`);
    }

    // Hide pagination
    const pagination = document.querySelector('.pagination-wrapper');
    if (pagination) {
      pagination.style.display = 'none';
    }
  }

  injectWishlistStyles() {
    // Check if styles already exist
    if (document.getElementById('wi-wishlist-styles')) return;

    const style = document.createElement('style');
    style.id = 'wi-wishlist-styles';
    style.textContent = `
      wi-wishlist {
        width: 100%;
        height: auto;
        display: flex;
        position: absolute;
        top: 0;
        left: 0;
        justify-content: flex-end;
        padding: 20px;
        z-index: 10;
      }
      .WI_wishList_in {
        width: 30px;
        height: 27px;
        border-radius: 30px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        pointer-events: all;
        position: relative;
      }
      .WI_wishlist_con {
        width: 30px;
        height: 27px;
        border-radius: 30px;
        display: flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        pointer-events: all;
        position: absolute;
        top: 0;
        left: 0;
      }
      .WI_wishlist_fill_con {
        width: 30px;
        height: 27px;
        border-radius: 30px;
        display: none;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        pointer-events: all;
        position: absolute;
        top: 0;
        left: 0;
      }
      .WI_wishlist_fill_con svg path {
        fill: #f60 !important;
        stroke: #f60;
      }
      svg.bi.bi-bookmark {
        width: 30px;
        height: 30px;
        fill: #f60;
      }
      svg.bi.bi-bookmark-check-fill {
        width: 30px;
        height: 30px;
        fill: #f60;
        stroke: #f60;
      }
    `;
    document.head.appendChild(style);
  }

  getCollectionHandle() {
    // Extract collection handle from URL
    const pathParts = window.location.pathname.split('/');
    const collectionsIndex = pathParts.indexOf('collections');
    if (collectionsIndex !== -1 && pathParts[collectionsIndex + 1]) {
      return pathParts[collectionsIndex + 1];
    }
    return null;
  }

  setupEventListeners() {
    // Listen for distance filter changes
    document.addEventListener('distanceFilterChange', (e) => {
      this.maxDistance = e.detail.distance;
      console.log('📏 Distance filter changed to:', this.maxDistance, 'miles');
      this.filterAndDisplayProducts();
    });

    // Listen for scroll events for infinite scroll
    window.addEventListener('scroll', () => this.handleScroll());

    // Listen for filter changes from Horizon theme's facet system
    document.addEventListener('filter:update', () => {
      this.handleFilterChange();
    });

    // Listen for URL changes (browser back/forward)
    window.addEventListener('popstate', () => {
      this.handleFilterChange();
    });
  }

  handleFilterChange() {
    console.log('🔄 handleFilterChange called');
    console.log('   Current URL:', window.location.href);

    // Show loading state
    document.body.classList.add('has-halo-loader');

    // Reset pagination when filters change
    this.currentCursor = null;
    this.hasNextPage = false;
    this.allProducts = [];

    // Build filters from URL to show what we're filtering by
    const filters = this.buildProductFiltersFromUrl();
    console.log('   Filters to apply:', filters);

    // Reload products with new filters
    console.log('   Calling loadProducts()...');
    this.loadProducts();
  }

  async requestLocation() {
    // Check if location is already stored in sessionStorage
    let storedLocation = sessionStorage.getItem('userLocation');

    /*storedLocation = JSON.stringify({
      latitude: 31.9759,
      longitude: -102.1159
    });*/

    if (storedLocation) {
      this.userLocation = JSON.parse(storedLocation);
      this.onLocationReceived();
      return;
    }

    // Show location permission modal
    this.showLocationModal();
  }

  showLocationModal() {
    const modal = document.getElementById('location-permission-modal');
    if (modal) {
      modal.classList.add('active');
    }
  }

  hideLocationModal() {
    const modal = document.getElementById('location-permission-modal');
    if (modal) {
      modal.classList.remove('active');
    }
  }

  async getLocation() {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by your browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          };
          this.userLocation = location;
          sessionStorage.setItem('userLocation', JSON.stringify(location));
          resolve(location);
        },
        (error) => {
          reject(error);
        }
      );
    });
  }

  /**
   * Looks up a US zip code and uses its coordinates as the user's location.
   * @param {string} zipcode - 5-digit (or ZIP+4) US zip code
   * @throws {Error} With a user-facing message when the zip code is invalid or can't be found
   */
  async setLocationFromZipcode(zipcode) {
    const zip = (zipcode || '').trim();
    if (!/^\d{5}(-\d{4})?$/.test(zip)) {
      throw new Error('Please enter a valid 5-digit zip code.');
    }

    const params = new URLSearchParams({
      postalcode: zip.slice(0, 5),
      country: 'us',
      format: 'json',
      limit: '1'
    });

    let results;
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?${params}`);
      if (!response.ok) throw new Error(`Status ${response.status}`);
      results = await response.json();
    } catch (error) {
      console.error('Zip code lookup failed:', error);
      throw new Error('Unable to look up that zip code right now. Please try again.');
    }

    const result = results?.[0];
    if (!result) {
      throw new Error(`We couldn't find zip code ${zip}. Please check it and try again.`);
    }

    this.userLocation = {
      latitude: parseFloat(result.lat),
      longitude: parseFloat(result.lon),
      zipcode: zip.slice(0, 5)
    };
    sessionStorage.setItem('userLocation', JSON.stringify(this.userLocation));
    sessionStorage.setItem('userZipcode', this.userLocation.zipcode);

    // Re-fetch products from the first page for the new location
    this.currentCursor = null;
    this.hasNextPage = false;
    this.allProducts = [];

    await this.onLocationReceived();
  }

  async onLocationReceived() {
    // Hide modal
    this.hideLocationModal();

    // Dispatch event for UI updates
    const event = new CustomEvent('locationReceived', {
      detail: this.userLocation
    });
    window.dispatchEvent(event);

    // Load products
    await this.loadProducts();
  }

  buildProductFiltersFromUrl(search = location.search) {
    const params = new URLSearchParams(search);
    const filters = [];

    // helper: push one filter per value (OR behaviour)
    const pushMany = (vals, factory) => {
      for (const v of vals) {
        const val = v.trim();
        if (val) filters.push(factory(val));
      }
    };

    // --- Product-level ---
    if (params.has('filter.p.product_type')) {
      pushMany(params.get('filter.p.product_type').split(','), v => ({ productType: v }));
    }
    if (params.has('filter.p.vendor')) {
      pushMany(params.get('filter.p.vendor').split(','), v => ({ productVendor: v }));
    }
    if (params.has('filter.p.tag')) {
      pushMany(params.get('filter.p.tag').split(','), v => ({ tag: v }));
    }
    // product metafield: filter.p.m.<namespace>.<key>=value[,value2]
    for (const [key, value] of params) {
      const m = key.match(/^filter\.p\.m\.([^.]+)\.([^.]+)$/);
      if (m) {
        const [, namespace, mfkey] = m;
        pushMany(value.split(','), v => ({
          productMetafield: {
            namespace,
            key: mfkey,
            value: v
          }
        }));
      }
    }

    // --- Variant-level ---
    // availability: 1(in stock) / 0(out of stock) / "0,1" (either)
    if (params.has('filter.v.availability')) {
      const raw = params.get('filter.v.availability');
      // If includes 1, treat as available true. If only 0, you could set available:false
      if (raw.split(',').includes('1')) filters.push({ available: true });
      else if (raw === '0') filters.push({ available: false });
    }

    // variant option: filter.v.option.<name>=value[,value2]
    for (const [key, value] of params) {
      const m = key.match(/^filter\.v\.option\.([^.]+)$/);
      if (m) {
        const optName = m[1];
        pushMany(value.split(','), v => ({
          variantOption: { name: optName, value: v }
        }));
      }
    }

    // price: filter.v.price.gte / filter.v.price.lte
    const gte = params.get('filter.v.price.gte');
    const lte = params.get('filter.v.price.lte');
    if (gte || lte) {
      const price = {};
      if (gte) price.min = parseFloat(gte);
      if (lte) price.max = parseFloat(lte);
      filters.push({ price });
    }

    // category: filter.p.t.category=aa-1__aa-2 etc. (single or list)
    if (params.has('filter.p.t.category')) {
      // Shopify uses taxonomy IDs like "aa-1" or joined with "__"
      // Storefront API expects { category: { id: "<id>" } }, push one per id.
      const ids = params.get('filter.p.t.category').split('__');
      pushMany(ids, id => ({ category: { id } }));
    }

    return filters;
  }

  getSortParamsFromUrl(search = location.search) {
    const params = new URLSearchParams(search);
    const sortBy = params.get('sort_by');

    if (!sortBy) {
      return { sortKey: null, reverse: false };
    }

    // Map Shopify sort_by values to GraphQL sortKey and reverse
    const sortMapping = {
      'manual': { sortKey: 'MANUAL', reverse: false },
      'best-selling': { sortKey: 'BEST_SELLING', reverse: false },
      'title-ascending': { sortKey: 'TITLE', reverse: false },
      'title-descending': { sortKey: 'TITLE', reverse: true },
      'price-ascending': { sortKey: 'PRICE', reverse: false },
      'price-descending': { sortKey: 'PRICE', reverse: true },
      'created-ascending': { sortKey: 'CREATED', reverse: false },
      'created-descending': { sortKey: 'CREATED', reverse: true }
    };

    return sortMapping[sortBy] || { sortKey: null, reverse: false };
  }

  async loadProducts(cursor = null) {
    if (this.isLoading) return;
    this.isLoading = true;

    try {
      const products = await this.fetchProductsFromStorefront(cursor);

      if (cursor) {
        // Append to existing products
        this.allProducts = [...this.allProducts, ...products.edges];
      } else {
        // Replace products
        this.allProducts = products.edges;
      }

      this.currentCursor = products.pageInfo.endCursor;
      this.hasNextPage = products.pageInfo.hasNextPage;

      // Filter and display (append if cursor was provided)
      this.filterAndDisplayProducts(!!cursor);

      // If using geo-location filtering and we have less than 20 filtered products,
      // continue fetching until we have at least 20 or no more pages
      if (this.userLocation && this.filteredProducts.length < 20 && this.hasNextPage) {
        console.log(`📦 Only ${this.filteredProducts.length} products found, fetching more...`);
        console.log(`   Fetched ${this.allProducts.length} total products so far`);
        this.isLoading = false; // Allow next fetch
        await this.loadProducts(this.currentCursor);
        return;
      }

      console.log(`✅ Finished loading: ${this.filteredProducts.length} products displayed`);

      // Remove loading state
      document.body.classList.remove('has-halo-loader');
    } catch (error) {
      console.error('Error loading products:', error);
      this.showError('Failed to load products. Please try again.');
      document.body.classList.remove('has-halo-loader');
    } finally {
      this.isLoading = false;
    }
  }

  async fetchProductsFromStorefront(cursor = null) {
    const query = `
      query CollectionProducts(
        $handle: String!
        $pageSize: Int!
        $cursor: String
        $filters: [ProductFilter!]
        $sortKey: ProductCollectionSortKeys
        $reverse: Boolean
      ) {
        collection(handle: $handle) {
          id
          title
          products(first: $pageSize, after: $cursor, filters: $filters, sortKey: $sortKey, reverse: $reverse) {
            pageInfo { hasNextPage endCursor }
            edges {
              cursor
              node {
                id
                handle
                title
                onlineStoreUrl
                availableForSale
                featuredImage { url altText }
                priceRange {
                  maxVariantPrice { amount currencyCode }
                  minVariantPrice { amount currencyCode }
                }
                variants(first: 1) {
                  edges {
                    node {
                      sku
                    }
                  }
                }
                metafields(identifiers: [
                  {
                    namespace: "zoho",
                    key: "msrp"
                  },
                  {
                    namespace: "zoho",
                    key: "model_year"
                  },
                  {
                    namespace: "zoho",
                    key: "brand"
                  },
                  {
                    namespace: "zoho",
                    key: "model"
                  },
                  {
                    namespace: "zoho",
                    key: "package_family"
                  },
                  {
                    namespace: "zoho",
                    key: "latitude"
                  },
                  {
                    namespace: "zoho",
                    key: "longitude"
                  },
                  {
                    namespace: "zoho",
                    key: "dealership_name"
                  }
                ]) {
                  key
                  value
                }
              }
            }
          }
        }
      }
    `;

    // Build filters from URL parameters
    const filters = this.buildProductFiltersFromUrl();

    // Get sort parameters from URL
    const sortParams = this.getSortParamsFromUrl();

    const variables = {
      handle: this.collectionHandle,
      pageSize: this.pageSize,
      cursor: cursor,
      filters: filters.length > 0 ? filters : null,
      sortKey: sortParams.sortKey || null,
      reverse: sortParams.reverse || false
    };

    console.log(window.system.storefrontAccessToken);

    const response = await fetch(`${window.location.origin}/api/2025-10/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.system.storefrontAccessToken || ''
      },
      body: JSON.stringify({ query, variables })
    });

    if (!response.ok) {
      throw new Error('Failed to fetch products from Storefront API');
    }

    const data = await response.json();

    if (data.errors) {
      throw new Error(data.errors[0].message);
    }

    return data.data.collection.products;
  }

  calculateDistance(lat1, lon1, lat2, lon2) {
    // Haversine formula to calculate distance in miles
    const R = 3959; // Earth's radius in miles
    const dLat = this.toRad(lat2 - lat1);
    const dLon = this.toRad(lon2 - lon1);

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.toRad(lat1)) * Math.cos(this.toRad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance;
  }

  toRad(degrees) {
    return degrees * (Math.PI / 180);
  }

  filterAndDisplayProducts(append = false) {
    if (!this.userLocation) {
      // No user location - show all products without distance filtering
      console.log('📍 No user location - showing all products without distance filtering');
      this.filteredProducts = this.allProducts;
      this.displayProducts(this.filteredProducts, append);
      return;
    }

    console.log('🔍 Filtering products with user location:', this.userLocation);
    console.log('   Max distance:', this.maxDistance, 'miles');
    console.log('   Total products to filter:', this.allProducts.length);

    // Calculate distance for all products and filter by distance
    this.filteredProducts = this.allProducts.map(edge => {
      const product = edge.node;
      const metafields = product.metafields || [];

      const latField = metafields.find(m => m && m.key === 'latitude');
      const lonField = metafields.find(m => m && m.key === 'longitude');

      if (latField && lonField) {
        const productLat = parseFloat(latField.value);
        const productLon = parseFloat(lonField.value);

        if (!isNaN(productLat) && !isNaN(productLon)) {
          const distance = this.calculateDistance(
            this.userLocation.latitude,
            this.userLocation.longitude,
            productLat,
            productLon
          );
          // Store distance on the product for display
          product.distance = distance;

          // Debug log for products with exact or very close coordinates
          if (distance < 1) {
            console.log(`   📍 Product "${product.title}" - Distance: ${distance.toFixed(2)} miles`);
            console.log(`      User: (${this.userLocation.latitude}, ${this.userLocation.longitude})`);
            console.log(`      Product: (${productLat}, ${productLon})`);
          }
        } else {
          console.warn(`   ⚠️ Invalid lat/long for product "${product.handle}":`, latField?.value, lonField?.value);
        }
      } else {
        console.warn(`   ⚠️ Missing lat/long for product "${product.handle}"`);
      }

      return edge;
    }).filter(edge => {
      const product = edge.node;

      // If user has location, exclude products without lat/long
      if (product.distance === undefined) {
        return false; // Don't show products without location data
      }

      // If maxDistance is set, filter by it
      if (this.maxDistance !== null && this.maxDistance > 0) {
        const included = product.distance <= this.maxDistance;
        if (!included && product.distance < this.maxDistance + 5) {
          console.log(`   ❌ Excluded "${product.title}" - ${product.distance.toFixed(2)} miles (max: ${this.maxDistance})`);
        }
        return included;
      }

      // No distance filter set, include all products with location
      return true;
    });

    // Sort by distance (closest first, products without distance at the end)
    this.filteredProducts.sort((a, b) => {
      const distA = a.node.distance !== undefined ? a.node.distance : Infinity;
      const distB = b.node.distance !== undefined ? b.node.distance : Infinity;
      return distA - distB;
    });

    console.log('   Filtered to', this.filteredProducts.length, 'products within', this.maxDistance, 'miles');

    if (this.filteredProducts.length > 0) {
      const closestDistance = this.filteredProducts[0].node.distance;
      const farthestDistance = this.filteredProducts[this.filteredProducts.length - 1].node.distance;
      console.log('   Distance range:', closestDistance?.toFixed(1), '-', farthestDistance?.toFixed(1), 'miles');
    }

    // Display filtered products
    this.displayProducts(this.filteredProducts, append);
  }

  displayProducts(products, append = false) {
    // Find the product grid UL element in Horizon theme
    let container = document.querySelector('.product-grid[data-testid="product-grid"]');
    if (!container) {
      console.error('Product grid not found');
      return;
    }

    console.log('🎨 displayProducts called with', products.length, 'products, append:', append);

    // Clear existing products if not appending
    if (!append) {
      container.innerHTML = '';
      this.displayedProductIds.clear();
      console.log('🧹 Cleared container');
    }

    // Show message if no products found within distance
    if (products.length === 0 && this.userLocation && this.maxDistance && !append) {
      console.log('⚠️ Showing empty state message');
      this.showEmptyState(true);
      this.updateProductCount(0);
      return;
    }

    if (products.length === 0 && !append) {
      this.showEmptyState(false);
      this.updateProductCount(0);
      return;
    }

    // Render each product (only new ones if appending)
    products.forEach(edge => {
      const productId = edge.node.id;

      // Skip if already displayed (for append mode)
      if (append && this.displayedProductIds.has(productId)) {
        return;
      }

      const productCard = this.createProductCard(edge.node);
      container.appendChild(productCard);
      this.displayedProductIds.add(productId);
    });

    // Update product count (total filtered products)
    this.updateProductCount(products.length);

    // Show load more button if there are more products to fetch
    this.updateLoadMoreButton();
  }

  createProductCard(product) {
    const li = document.createElement('li');
    li.className = 'product-grid__item';
    li.setAttribute('data-product-id', product.id);
    li.setAttribute('data-location-filtered', 'true');

    const distance = product.distance !== undefined ? product.distance.toFixed(1) : null;

    const featuredImageUrl = product.featuredImage?.url || '';
    const featuredImageAlt = product.featuredImage?.altText || product.title;
    const getMf = (key) => {
      const metafield = (product.metafields || []).find(m => m && m.key === key);
      return metafield?.value?.toString().trim() || '';
    };
    const composedTitle = [getMf('model_year'), getMf('brand'), getMf('model')]
      .filter(Boolean)
      .join(' ')
      .trim();
    const productTitle = composedTitle || product.title;
    const minPrice = product.priceRange?.minVariantPrice;
    const available = product.availableForSale !== false;
    const productHandle = product.handle;
    // Scope to the current collection so the product page can derive it for breadcrumbs
    const productUrl = `/collections/${this.collectionHandle}/products/${productHandle}`;
    const msrp = getMf('msrp');
    const packageFamily = getMf('package_family');
    const dealershipName = getMf('dealership_name');
    const sku = product.variants?.edges?.[0]?.node?.sku || '';

    // Format price
    const priceToShow = msrp || minPrice?.amount;
    const formattedPrice = priceToShow ? this.formatPrice({ amount: priceToShow, currencyCode: minPrice?.currencyCode || 'USD' }) : '';

    li.innerHTML = `
      <product-card class="product-card location-product-card" data-product-id="${product.id}">
        <a href="${productUrl}" class="product-card__link" ref="productCardLink">
          <span class="visually-hidden">${productTitle}</span>
        </a>
        <div class="product-card__content layout-panel-flex layout-panel-flex--column product-grid__card spacing-style border-style gap-style">
          <div class="product-media-container">
            <div class="product-media">
              ${featuredImageUrl ? `<img src="${featuredImageUrl}" alt="${featuredImageAlt}" loading="lazy" class="product-media__image">` : `<img src="https://cdn.shopify.com/s/files/1/0771/2528/8161/files/ffv-placeholder-optimized.png?v=1761897561" alt="Placeholder" loading="lazy" class="product-media__image">`}
            </div>
            <wi-wishlist>
              <div class="WI_wishList_in">
                <div class="WI_wishlist_con wishID wish_${productHandle}" data-producthandel="${productHandle}" id="wish_${productHandle}">
                  <svg xmlns="http://www.w3.org/2000/svg" class="bi bi-bookmark" viewBox="0 0 16 16">
                    <path d="M2 2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.777.416L8 13.101l-5.223 2.815A.5.5 0 0 1 2 15.5zm2-1a1 1 0 0 0-1 1v12.566l4.723-2.482a.5.5 0 0 1 .554 0L13 14.566V2a1 1 0 0 0-1-1z"/>
                  </svg>
                </div>
                <div class="WI_wishlist_fill_con wishID wish_remove_${productHandle}" data-producthandel="${productHandle}" id="wish_remove_${productHandle}" style="display: none;">
                  <svg xmlns="http://www.w3.org/2000/svg" class="bi bi-bookmark-check-fill" viewBox="0 0 16 16">
                    <path fill-rule="evenodd" d="M2 15.5V2a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v13.5a.5.5 0 0 1-.74.439L8 13.069l-5.26 2.87A.5.5 0 0 1 2 15.5m8.854-9.646a.5.5 0 0 0-.708-.708L7.5 7.793 6.354 6.646a.5.5 0 1 0-.708.708l1.5 1.5a.5.5 0 0 0 .708 0z"/>
                  </svg>
                </div>
              </div>
            </wi-wishlist>
          </div>
          <div class="product-card__info">
            <div class="product-card__title-wrapper">
              <h3 class="product-card__title">
                <a href="${productUrl}">
                  ${productTitle}
                </a>
              </h3>
              ${packageFamily ? `<div class="product-card__package">${packageFamily}</div>` : ''}
            </div>

            ${formattedPrice ? `
              <div class="product-card__price-section">
                <div class="product-card__msrp-label">
                  <span>MSRP</span>
                  <span class="product-card__info-icon">?
                    <span class="product-card__tooltip">Manufacturer's Suggested Retail Price (MSRP) for vehicle as shown does not necessarily represent the dealer's actual sale price and does not include tax, title, license, registration and adjusted market value. Dealer sets actual price. Please consult your selected dealer.</span>
                  </span>
                </div>
                <div class="product-card__price">${formattedPrice}</div>
              </div>
            ` : ''}

            <div class="product-card__availability">
              <span class="product-card__availability-text${!available ? ' product-card__availability-text--out-of-stock' : ''}">${available ? 'AVAILABLE NOW' : 'OUT OF STOCK'}</span>
              <span class="product-card__info-icon product-card__info-icon--orange">?
                <span class="product-card__tooltip">Vehicle is located at this dealership. Due to demand, availability is subject to change without notice. Please contact this dealership for additional, and most up to date information about this vehicle.</span>
              </span>
            </div>

            ${dealershipName ? `
              <div class="product-card__dealer">
                ${dealershipName}${distance !== null ? ` <span class="product-card__distance">(${distance} mi)</span>` : ''}
              </div>
            ` : ''}

            ${sku ? `<div class="product-card__vin">VIN: ${sku}</div>` : ''}

            <a href="${productUrl}" class="product-card__button">
              VEHICLE DETAILS
            </a>
          </div>
        </div>
      </product-card>
    `;

    return li;
  }

  initializeWishlist(productCard, productHandle) {
    // The wi-wishlist custom element handles all wishlist functionality
    // including event listeners, so we don't need to add them here.
    // The custom element's connectedCallback() automatically runs when inserted into DOM.

    // Just check if product is already in wishlist and show correct icon
    const wishlistElement = productCard.querySelector('wi-wishlist');
    if (!wishlistElement) return;

    const wishListCon = wishlistElement.querySelector('.WI_wishlist_con');
    const wishListRemove = wishlistElement.querySelector('.WI_wishlist_fill_con');

    const savedWishlist = JSON.parse(localStorage.getItem('productHandleArray')) || [];
    if (savedWishlist.includes(productHandle)) {
      if (wishListCon) wishListCon.style.display = 'none';
      if (wishListRemove) wishListRemove.style.display = 'flex';
    } else {
      if (wishListCon) wishListCon.style.display = 'flex';
      if (wishListRemove) wishListRemove.style.display = 'none';
    }
  }

  updateWishlistCount() {
    const dataForCount = JSON.parse(localStorage.getItem('productHandleArray')) || [];
    const countElement = document.querySelector('.WI_wishListCount');
    if (countElement) {
      if (dataForCount.length === 0) {
        countElement.style.display = 'none';
      } else {
        countElement.style.display = 'block';
        countElement.innerHTML = dataForCount.length;
      }
    }
  }

  formatPrice(price) {
    const formatter = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: price.currencyCode
    });
    return formatter.format(parseFloat(price.amount));
  }

  formatPriceRange(minPrice, maxPrice) {
    if (!minPrice) return '';

    const minFormatted = this.formatPrice(minPrice);

    if (!maxPrice || minPrice.amount === maxPrice.amount) {
      return minFormatted;
    }

    const maxFormatted = this.formatPrice(maxPrice);
    return `${minFormatted} - ${maxFormatted}`;
  }

  showEmptyState(hasLocation = false) {
    const container = document.querySelector('.product-grid[data-testid="product-grid"]');
    if (!container) return;

    if (hasLocation) {
      // Show distance-specific empty state
      container.innerHTML = `
        <li style="grid-column: 1 / -1; padding: 60px 20px; text-align: center;">
          <div style="max-width: 500px; margin: 0 auto;">
            <svg width="80" height="80" viewBox="0 0 24 24" fill="none" style="margin-bottom: 20px;">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill="#d1d5db"/>
            </svg>
            <h2 style="font-size: 24px; font-weight: 600; color: #111827; margin-bottom: 12px;">
              No Products Within Your Region
            </h2>
            <p style="font-size: 16px; color: #6b7280; margin-bottom: 24px; line-height: 1.5;">
              We couldn't find any products within <strong>${this.maxDistance} miles</strong> of your location.
            </p>
            <div style="display: flex; flex-direction: column; gap: 12px; max-width: 300px; margin: 0 auto;">
              <button
                onclick="document.getElementById('distance-select').value='250'; document.getElementById('distance-select').dispatchEvent(new Event('change'));"
                style="padding: 12px 24px; background: #ff6a00; color: white; border: none; border-radius: 8px; font-size: 15px; font-weight: 600; cursor: pointer;"
              >
                Expand Search to 250 Miles
              </button>
              <button
                onclick="document.getElementById('change-location-btn').click();"
                style="padding: 12px 24px; background: #f3f4f6; color: #374151; border: none; border-radius: 8px; font-size: 15px; font-weight: 600; cursor: pointer;"
              >
                Change Location
              </button>
            </div>
          </div>
        </li>
      `;
    } else {
      // Show generic empty state
      container.innerHTML = `
        <li style="grid-column: 1 / -1; padding: 60px 20px; text-align: center;">
          <div class="main-collection-grid__empty">
            <h2 class="main-collection-grid__empty-title h2">No products found</h2>
            <p>Try adjusting your filters or check back later.</p>
          </div>
        </li>
      `;
    }
  }

  updateProductCount(count) {
    const countElement = document.querySelector('.results-count .count');
    if (countElement) {
      countElement.textContent = count;
    }

    // Also update the product count text
    const resultsElement = document.querySelector('.results-count .results');
    if (resultsElement) {
      resultsElement.textContent = `${count} ${count === 1 ? 'product' : 'products'}`;
    }
  }

  updateLoadMoreButton() {
    let loadMoreBtn = document.getElementById('load-more-products');
    const gridContainer = document.querySelector('.product-grid[data-testid="product-grid"]');

    if (!loadMoreBtn && gridContainer && gridContainer.parentElement) {
      // Create load more button after the grid
      loadMoreBtn = document.createElement('div');
      loadMoreBtn.id = 'load-more-products';
      loadMoreBtn.className = 'infinite-pagination';
      loadMoreBtn.style.cssText = 'grid-column: 1 / -1; text-align: center; padding: 20px;';
      gridContainer.parentElement.appendChild(loadMoreBtn);
    }

    if (loadMoreBtn) {
      if (this.hasNextPage) {
        loadMoreBtn.innerHTML = `
          <button class="button" id="load-more-btn" style="padding: 12px 24px; cursor: pointer;">
            Load More Products
          </button>
        `;

        const btn = loadMoreBtn.querySelector('#load-more-btn');
        btn.addEventListener('click', () => this.loadMoreProducts());
      } else {
        loadMoreBtn.innerHTML = '';
      }
    }
  }

  async loadMoreProducts() {
    if (!this.hasNextPage || this.isLoading) return;

    console.log('📄 Loading more products from cursor:', this.currentCursor);
    await this.loadProducts(this.currentCursor);
  }

  handleScroll() {
    // Infinite scroll: load more when near bottom
    const scrollPosition = window.innerHeight + window.scrollY;
    const threshold = document.documentElement.scrollHeight - 500;

    if (scrollPosition >= threshold && this.hasNextPage && !this.isLoading) {
      this.loadMoreProducts();
    }
  }

  showError(message) {
    // You can customize this to match your theme's error display
    console.error(message);
    alert(message);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    console.log('📍 Initializing LocationFilter');
    window.locationFilter = new LocationFilter();
  });
} else {
  console.log('📍 Initializing LocationFilter (DOM already ready)');
  window.locationFilter = new LocationFilter();
}
